import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageCircle,
  Send,
  Search,
  Filter,
  Phone,
  Clock,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  User,
  Calendar,
  MoreVertical,
  Reply,
  Heart,
  ThumbsDown,
  Zap,
  Eye,
  ChevronDown,
  Plus,
  X,
  Settings,
  Archive,
  Star,
  Mic,
  Image,
  Paperclip,
  Smile,
  ArrowUp,
  ArrowDown,
  Activity,
  Users,
  Bell,
  Loader2,
} from 'lucide-react';
import { Button } from '../../components/ui/Button/Button';
import { cn } from '../../utils/cn';

// Types for SMS Conversations
interface SMSConversation {
  id: string;
  contactPhone: string;
  contactName?: string;
  fromNumber: string;
  status: 'active' | 'paused' | 'opt_out' | 'completed';
  messageCount: number;
  lastMessageAt?: Date;
  lastMessageDirection?: 'inbound' | 'outbound';
  assignedTo?: string;
  tags: string[];
  sentiment: 'positive' | 'negative' | 'neutral';
  priority: 'low' | 'medium' | 'high';
  createdAt: Date;
  messages?: SMSMessage[];
  traceId?: string;
}

interface SMSMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  messageBody: string;
  status: 'queued' | 'sent' | 'delivered' | 'failed' | 'read';
  sentAt?: Date;
  deliveredAt?: Date;
  createdAt: Date;
  replies?: SMSReply[];
}

interface SMSReply {
  id: string;
  replyContent: string;
  sentiment: 'positive' | 'negative' | 'neutral';
  intent: string;
  urgency: 'low' | 'medium' | 'high';
  confidence: number;
  createdAt: Date;
}

interface ConversationAnalytics {
  totalMessages: number;
  inboundMessages: number;
  outboundMessages: number;
  readMessages: number;
  readRate: number;
  avgResponseTime: number;
  sentimentBreakdown: Record<string, number>;
  engagementScore: number;
  conversationDuration: number;
}

interface SummaryStats {
  totalConversations: number;
  activeConversations: number;
  optOutConversations: number;
  optOutRate: number;
  sentimentBreakdown: Record<string, number>;
  priorityBreakdown: Record<string, number>;
  readReceiptAnalytics: any;
}

// API Service
class SMSConversationService {
  private baseUrl = '/api/sms/conversations';

  async getConversations(params: {
    page?: number;
    limit?: number;
    status?: string;
    sentiment?: string;
    priority?: string;
    search?: string;
  }) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const response = await fetch(`${this.baseUrl}?${queryParams}`);
    return response.json();
  }

  async getConversation(id: string): Promise<SMSConversation> {
    const response = await fetch(`${this.baseUrl}/${id}`);
    return response.json();
  }

  async updateConversation(id: string, updates: Partial<SMSConversation>) {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    return response.json();
  }

  async sendReply(conversationId: string, message: string, fromNumber: string) {
    const response = await fetch(`${this.baseUrl}/${conversationId}/reply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, fromNumber }),
    });
    return response.json();
  }

  async getAnalytics(conversationId: string): Promise<ConversationAnalytics> {
    const response = await fetch(`${this.baseUrl}/${conversationId}/analytics`);
    return response.json();
  }

  async getSummaryStats(): Promise<SummaryStats> {
    const response = await fetch(`${this.baseUrl}/stats/summary`);
    return response.json();
  }
}

// Components
const StatCard: React.FC<{
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  color: string;
  trend?: { value: number; direction: 'up' | 'down' };
}> = ({ title, value, subtitle, icon: Icon, color, trend }) => (
  <motion.div
    className="bg-gradient-to-br from-neutral-900/50 via-neutral-800/30 to-neutral-900/50 backdrop-blur-xl border border-neutral-800/50 rounded-2xl p-6 hover:border-neutral-700/50 transition-all duration-300"
    whileHover={{ scale: 1.02, y: -2 }}
    whileTap={{ scale: 0.98 }}
  >
    <div className="flex items-start justify-between mb-4">
      <div className={cn("p-3 rounded-xl bg-gradient-to-br", color)}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      {trend && (
        <div className={cn(
          "flex items-center text-sm font-medium",
          trend.direction === 'up' ? 'text-green-400' : 'text-red-400'
        )}>
          {trend.direction === 'up' ? <ArrowUp className="w-4 h-4 mr-1" /> : <ArrowDown className="w-4 h-4 mr-1" />}
          {trend.value}%
        </div>
      )}
    </div>
    <div className="space-y-1">
      <h3 className="text-2xl font-bold text-white">{value}</h3>
      <p className="text-sm text-neutral-400">{title}</p>
      {subtitle && <p className="text-xs text-neutral-500">{subtitle}</p>}
    </div>
  </motion.div>
);

const ConversationListItem: React.FC<{
  conversation: SMSConversation;
  isSelected: boolean;
  onClick: () => void;
}> = ({ conversation, isSelected, onClick }) => {
  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return 'text-green-400 bg-green-400/10';
      case 'negative': return 'text-red-400 bg-red-400/10';
      default: return 'text-yellow-400 bg-yellow-400/10';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-400 bg-red-400/10';
      case 'medium': return 'text-yellow-400 bg-yellow-400/10';
      default: return 'text-blue-400 bg-blue-400/10';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-400 bg-green-400/10';
      case 'paused': return 'text-yellow-400 bg-yellow-400/10';
      case 'opt_out': return 'text-red-400 bg-red-400/10';
      default: return 'text-neutral-400 bg-neutral-400/10';
    }
  };

  return (
    <motion.div
      className={cn(
        "p-4 border border-neutral-800/50 rounded-xl cursor-pointer transition-all duration-200",
        "hover:border-neutral-700/50 hover:bg-neutral-800/20",
        isSelected ? "border-blue-500/50 bg-blue-500/5" : "bg-neutral-900/20"
      )}
      onClick={onClick}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
            <User className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-white font-medium text-sm">
              {conversation.contactName || conversation.contactPhone}
            </h3>
            <p className="text-neutral-400 text-xs">{conversation.contactPhone}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className={cn("px-2 py-1 rounded-lg text-xs font-medium", getPriorityColor(conversation.priority))}>
            {conversation.priority}
          </span>
          <span className={cn("px-2 py-1 rounded-lg text-xs font-medium", getStatusColor(conversation.status))}>
            {conversation.status}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-neutral-400">
        <div className="flex items-center space-x-4">
          <span className="flex items-center">
            <MessageCircle className="w-3 h-3 mr-1" />
            {conversation.messageCount}
          </span>
          <span className={cn("px-2 py-1 rounded-lg", getSentimentColor(conversation.sentiment))}>
            {conversation.sentiment}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          {conversation.lastMessageAt && (
            <span className="flex items-center">
              <Clock className="w-3 h-3 mr-1" />
              {new Date(conversation.lastMessageAt).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>

      {conversation.tags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {conversation.tags.slice(0, 3).map((tag, index) => (
            <span key={index} className="px-2 py-1 bg-neutral-700/50 text-neutral-300 text-xs rounded-md">
              {tag}
            </span>
          ))}
          {conversation.tags.length > 3 && (
            <span className="px-2 py-1 bg-neutral-700/50 text-neutral-300 text-xs rounded-md">
              +{conversation.tags.length - 3}
            </span>
          )}
        </div>
      )}
    </motion.div>
  );
};

const MessageBubble: React.FC<{
  message: SMSMessage;
  isOutbound: boolean;
}> = ({ message, isOutbound }) => (
  <motion.div
    className={cn(
      "flex mb-4",
      isOutbound ? "justify-end" : "justify-start"
    )}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
  >
    <div className={cn(
      "max-w-[70%] px-4 py-3 rounded-2xl",
      isOutbound 
        ? "bg-gradient-to-br from-blue-500 to-purple-600 text-white rounded-br-md"
        : "bg-neutral-800/50 text-white rounded-bl-md"
    )}>
      <p className="text-sm">{message.messageBody}</p>
      <div className="flex items-center justify-between mt-2 text-xs opacity-70">
        <span>{new Date(message.createdAt).toLocaleTimeString()}</span>
        {isOutbound && (
          <div className="flex items-center space-x-1">
            {message.status === 'delivered' && <CheckCircle2 className="w-3 h-3" />}
            {message.status === 'read' && <Eye className="w-3 h-3" />}
            {message.status === 'failed' && <AlertCircle className="w-3 h-3" />}
          </div>
        )}
      </div>
    </div>
  </motion.div>
);

const ConversationDetail: React.FC<{
  conversation: SMSConversation;
  analytics: ConversationAnalytics | null;
  onSendReply: (message: string, fromNumber: string) => Promise<void>;
  onUpdateConversation: (updates: Partial<SMSConversation>) => Promise<void>;
}> = ({ conversation, analytics, onSendReply, onUpdateConversation }) => {
  const [replyMessage, setReplyMessage] = useState('');
  const [fromNumber, setFromNumber] = useState(conversation.fromNumber);
  const [sending, setSending] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);

  const handleSendReply = async () => {
    if (!replyMessage.trim()) return;
    
    setSending(true);
    try {
      await onSendReply(replyMessage, fromNumber);
      setReplyMessage('');
    } catch (error) {
      console.error('Failed to send reply:', error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-neutral-900/50 via-neutral-800/30 to-neutral-900/50 backdrop-blur-xl border border-neutral-800/50 rounded-2xl">
      {/* Header */}
      <div className="p-6 border-b border-neutral-800/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {conversation.contactName || conversation.contactPhone}
              </h2>
              <p className="text-neutral-400">{conversation.contactPhone}</p>
              {conversation.traceId && (
                <a
                  href={`/api-log?traceId=${conversation.traceId}`}
                  className="text-xs text-blue-400 hover:underline mt-1 inline-block"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="View API Log for this traceId"
                >
                  Trace ID: {conversation.traceId}
                </a>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="bg-neutral-800/50 border-neutral-700/50 text-neutral-300 hover:bg-neutral-700/50"
            >
              <Activity className="w-4 h-4 mr-2" />
              Analytics
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="bg-neutral-800/50 border-neutral-700/50 text-neutral-300 hover:bg-neutral-700/50"
            >
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Analytics Panel */}
        <AnimatePresence>
          {showAnalytics && analytics && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 p-4 bg-neutral-800/20 rounded-lg border border-neutral-700/30"
            >
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-white">{analytics.totalMessages}</p>
                  <p className="text-xs text-neutral-400">Total Messages</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-400">{analytics.readRate.toFixed(1)}%</p>
                  <p className="text-xs text-neutral-400">Read Rate</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-400">{analytics.avgResponseTime}m</p>
                  <p className="text-xs text-neutral-400">Avg Response</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-400">{analytics.engagementScore.toFixed(1)}</p>
                  <p className="text-xs text-neutral-400">Engagement</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Messages */}
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="space-y-1">
          {conversation.messages?.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              isOutbound={message.direction === 'outbound'}
            />
          ))}
        </div>
      </div>

      {/* Reply Input */}
      <div className="p-6 border-t border-neutral-800/50">
        <div className="flex items-end space-x-3">
          <div className="flex-1">
            <textarea
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              placeholder="Type your message..."
              className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700/50 rounded-xl text-white placeholder-neutral-400 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
              rows={3}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  handleSendReply();
                }
              }}
            />
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center space-x-2 text-xs text-neutral-400">
                <span>{replyMessage.length}/160</span>
                <span>•</span>
                <span>From: {fromNumber}</span>
              </div>
              <div className="text-xs text-neutral-500">
                ⌘ + Enter to send
              </div>
            </div>
          </div>
          <Button
            onClick={handleSendReply}
            disabled={!replyMessage.trim() || sending}
            className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 px-6 py-3"
          >
            {sending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main SMS Conversations Component
export const SMSConversations: React.FC = () => {
  const [conversations, setConversations] = useState<SMSConversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<SMSConversation | null>(null);
  const [loading, setLoading] = useState(false);
  const [summaryStats, setSummaryStats] = useState<SummaryStats | null>(null);
  const [analytics, setAnalytics] = useState<ConversationAnalytics | null>(null);
  const [filters, setFilters] = useState({
    status: '',
    sentiment: '',
    priority: '',
    search: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  const smsService = new SMSConversationService();

  // Load conversations
  const loadConversations = useCallback(async () => {
    setLoading(true);
    try {
      const result = await smsService.getConversations({
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      });
      setConversations(result.conversations || []);
      setPagination(prev => ({
        ...prev,
        total: result.pagination?.total || 0,
        pages: result.pagination?.pages || 0,
      }));
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  // Load summary stats
  const loadSummaryStats = useCallback(async () => {
    try {
      const stats = await smsService.getSummaryStats();
      setSummaryStats(stats);
    } catch (error) {
      console.error('Failed to load summary stats:', error);
    }
  }, []);

  // Load conversation details
  const loadConversationDetails = async (conversationId: string) => {
    try {
      const conversation = await smsService.getConversation(conversationId);
      const conversationAnalytics = await smsService.getAnalytics(conversationId);
      
      setSelectedConversation(conversation);
      setAnalytics(conversationAnalytics);
    } catch (error) {
      console.error('Failed to load conversation details:', error);
    }
  };

  // Send reply
  const handleSendReply = async (message: string, fromNumber: string) => {
    if (!selectedConversation) return;
    
    try {
      await smsService.sendReply(selectedConversation.id, message, fromNumber);
      await loadConversationDetails(selectedConversation.id);
      await loadConversations();
    } catch (error) {
      console.error('Failed to send reply:', error);
      throw error;
    }
  };

  // Update conversation
  const handleUpdateConversation = async (updates: Partial<SMSConversation>) => {
    if (!selectedConversation) return;
    
    try {
      await smsService.updateConversation(selectedConversation.id, updates);
      await loadConversationDetails(selectedConversation.id);
      await loadConversations();
    } catch (error) {
      console.error('Failed to update conversation:', error);
      throw error;
    }
  };

  useEffect(() => {
    loadConversations();
    loadSummaryStats();
  }, [loadConversations, loadSummaryStats]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
              SMS Conversations
            </h1>
            <p className="text-neutral-400 mt-1">Manage your SMS conversations and track engagement</p>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              className="bg-neutral-800/50 border-neutral-700/50 text-neutral-300 hover:bg-neutral-700/50"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
            <Button className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700">
              <Plus className="w-4 h-4 mr-2" />
              New Conversation
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {summaryStats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Total Conversations"
              value={summaryStats.totalConversations.toLocaleString()}
              subtitle={`${summaryStats.activeConversations} active`}
              icon={MessageCircle}
              color="from-blue-500 to-blue-600"
              trend={{ value: 12, direction: 'up' }}
            />
            <StatCard
              title="Response Rate"
              value={`${((summaryStats.activeConversations / Math.max(summaryStats.totalConversations, 1)) * 100).toFixed(1)}%`}
              subtitle="Last 30 days"
              icon={Reply}
              color="from-green-500 to-green-600"
              trend={{ value: 8, direction: 'up' }}
            />
            <StatCard
              title="Opt-out Rate"
              value={`${summaryStats.optOutRate.toFixed(1)}%`}
              subtitle={`${summaryStats.optOutConversations} opt-outs`}
              icon={Bell}
              color="from-red-500 to-red-600"
              trend={{ value: 3, direction: 'down' }}
            />
            <StatCard
              title="Avg Engagement"
              value="94.2%"
              subtitle="Read receipts"
              icon={Activity}
              color="from-purple-500 to-purple-600"
              trend={{ value: 5, direction: 'up' }}
            />
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-gradient-to-br from-neutral-900/50 via-neutral-800/30 to-neutral-900/50 backdrop-blur-xl border border-neutral-800/50 rounded-2xl p-6">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-10 pr-4 py-3 bg-neutral-800/50 border border-neutral-700/50 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
              />
            </div>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="px-4 py-3 bg-neutral-800/50 border border-neutral-700/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="opt_out">Opt Out</option>
              <option value="completed">Completed</option>
            </select>
            <select
              value={filters.sentiment}
              onChange={(e) => setFilters(prev => ({ ...prev, sentiment: e.target.value }))}
              className="px-4 py-3 bg-neutral-800/50 border border-neutral-700/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            >
              <option value="">All Sentiment</option>
              <option value="positive">Positive</option>
              <option value="neutral">Neutral</option>
              <option value="negative">Negative</option>
            </select>
            <select
              value={filters.priority}
              onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
              className="px-4 py-3 bg-neutral-800/50 border border-neutral-700/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            >
              <option value="">All Priority</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 min-h-[600px]">
          {/* Conversations List */}
          <div className="lg:col-span-1 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">
                Conversations ({conversations.length})
              </h2>
              {loading && <Loader2 className="w-5 h-5 animate-spin text-blue-400" />}
            </div>
            
            <div className="space-y-3 max-h-[700px] overflow-y-auto">
              {conversations.map((conversation) => (
                <ConversationListItem
                  key={conversation.id}
                  conversation={conversation}
                  isSelected={selectedConversation?.id === conversation.id}
                  onClick={() => loadConversationDetails(conversation.id)}
                />
              ))}
              
              {conversations.length === 0 && !loading && (
                <div className="text-center py-12">
                  <MessageCircle className="w-12 h-12 text-neutral-500 mx-auto mb-4" />
                  <p className="text-neutral-400">No conversations found</p>
                  <p className="text-sm text-neutral-500 mt-1">Start a new conversation to get started</p>
                </div>
              )}
            </div>
          </div>

          {/* Conversation Detail */}
          <div className="lg:col-span-2">
            {selectedConversation ? (
              <ConversationDetail
                conversation={selectedConversation}
                analytics={analytics}
                onSendReply={handleSendReply}
                onUpdateConversation={handleUpdateConversation}
              />
            ) : (
              <div className="h-full bg-gradient-to-br from-neutral-900/50 via-neutral-800/30 to-neutral-900/50 backdrop-blur-xl border border-neutral-800/50 rounded-2xl flex items-center justify-center">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 text-neutral-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">Select a conversation</h3>
                  <p className="text-neutral-400">Choose a conversation from the list to view details and messages</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMSConversations; 